import React from 'react';
import { Link } from 'react-router-dom';
import CompanyDetailsBlock from '../components/CompanyDetailsBlock';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1
          className="text-4xl sm:text-5xl tracking-tight leading-none font-bold text-[#112318] mb-4"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="terms-title"
        >
          Terms & Conditions
        </h1>
        <p className="text-sm text-[#4A5D50] mb-12">Effective Date: 14 April 2026</p>

        <div className="prose prose-sm max-w-none space-y-10 text-[#4A5D50]">

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              1. Introduction
            </h2>
            <p className="leading-relaxed">
              Welcome to VinePass ("we", "our", "us"). These Terms and Conditions govern your use of
              our website (<a href="https://vinepass.co.za" className="text-[#1F3D2B] font-semibold hover:underline">vinepass.co.za</a>)
              and services.
            </p>
            <p className="leading-relaxed mt-3">
              By accessing or using VinePass, you agree to be bound by these Terms and Conditions.
              If you do not agree, please do not use our platform.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              2. Nature of the Service
            </h2>
            <p className="leading-relaxed">
              VinePass is an online ticketing platform that enables users to discover and purchase
              tickets for events hosted by third-party event organisers. VinePass acts as an
              intermediary and is not the organiser of events listed on the platform unless
              explicitly stated.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              3. User Accounts
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>You may be required to provide accurate personal information when creating an account or purchasing tickets.</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials and personal information.</li>
              <li>You agree not to use the platform for any unlawful or fraudulent purpose.</li>
              <li>You must notify us immediately of any unauthorised use of your account.</li>
              <li>You accept responsibility for all activities that occur under your account.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              4. Ticket Purchases
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>All ticket purchases are subject to availability.</li>
              <li>Prices are set by event organisers and may vary. All prices are displayed in South African Rand (ZAR).</li>
              <li>Once a ticket is purchased, you will receive confirmation via email to the address provided.</li>
              <li>It is your responsibility to ensure that your personal and contact details are correct at the time of purchase.</li>
              <li>Tickets are non-transferable unless otherwise stated by the event organiser.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              5. Payments
            </h2>
            <p className="leading-relaxed">
              All payments are processed securely through trusted third-party payment providers,
              including PayFast and Stripe. VinePass does not store full payment or banking details.
              By making a purchase, you agree to the respective payment provider's terms of service.
              All transactions are conducted in South African Rand (ZAR).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              6. Refunds & Cancellations
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Refund policies are determined by the event organiser.</li>
              <li>VinePass is not responsible for issuing refunds unless otherwise stated.</li>
              <li>In the event of a cancellation, the organiser is responsible for communicating with ticket holders and managing refunds.</li>
              <li>If an event is rescheduled, tickets remain valid for the new date. Refund requests may be submitted if the new date is unsuitable, subject to the organiser's discretion.</li>
              <li>Refunds, where applicable, will be processed within 7–14 business days.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              7. Event Changes
            </h2>
            <p className="leading-relaxed mb-3">
              Event organisers reserve the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Change event dates, times, or venues.</li>
              <li>Modify event content, lineups, or programmes.</li>
              <li>Cancel events if necessary due to unforeseen circumstances.</li>
            </ul>
            <p className="leading-relaxed mt-3">
              VinePass will not be held liable for any such changes. We will, however, make
              reasonable efforts to communicate updates received from organisers to affected
              ticket holders.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              8. Event Organisers
            </h2>
            <p className="leading-relaxed mb-3">
              If you list events on VinePass as an organiser, you agree that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>All event information provided must be accurate, complete, and truthful.</li>
              <li>You are solely responsible for delivering the event as described on the platform.</li>
              <li>You will comply with all applicable South African laws and regulations, including health and safety requirements.</li>
              <li>VinePass may remove events that violate our policies or guidelines without prior notice.</li>
              <li>VinePass may charge a service fee on ticket sales, the details of which will be communicated to you.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              9. Limitation of Liability
            </h2>
            <p className="leading-relaxed mb-3">
              VinePass provides the platform on an "as is" basis. To the fullest extent permitted
              by South African law, VinePass is not liable for:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Any loss, injury, damage, or expense sustained at events.</li>
              <li>Event cancellations, postponements, or changes by organisers.</li>
              <li>Any issues arising from the conduct of third-party event organisers.</li>
              <li>Any indirect, incidental, or consequential damages arising from your use of the platform.</li>
            </ul>
            <p className="leading-relaxed mt-3">
              Use of the platform and attendance at events is at your own risk.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              10. Prohibited Conduct
            </h2>
            <p className="leading-relaxed mb-3">You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Use the platform for any fraudulent, deceptive, or unlawful purposes.</li>
              <li>Resell tickets at inflated prices (scalping) unless authorised by the organiser.</li>
              <li>Submit false, misleading, or fraudulent information.</li>
              <li>Attempt to gain unauthorised access to the platform, its systems, or other user accounts.</li>
              <li>Interfere with or disrupt the operation of the website or its infrastructure.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              11. Intellectual Property
            </h2>
            <p className="leading-relaxed">
              All content on VinePass, including but not limited to branding, logos, website design,
              graphics, and text, is the property of VinePass and is protected by applicable
              intellectual property laws. This content may not be reproduced, distributed, or used
              without the express written permission of VinePass.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              12. Privacy
            </h2>
            <p className="leading-relaxed">
              Your use of the platform is also governed by our{' '}
              <Link to="/privacy" className="text-[#1F3D2B] font-semibold hover:underline" data-testid="terms-privacy-link">
                Privacy Policy
              </Link>
              , which details how we collect, store, and process your personal information in
              compliance with the Protection of Personal Information Act (POPIA) of South Africa.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              13. Governing Law
            </h2>
            <p className="leading-relaxed">
              These Terms and Conditions are governed by and construed in accordance with the laws
              of the Republic of South Africa. Any disputes arising from the use of this platform
              shall be subject to the exclusive jurisdiction of the courts of South Africa.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              14. Changes to Terms
            </h2>
            <p className="leading-relaxed">
              We reserve the right to update or modify these Terms and Conditions at any time.
              Updated Terms will be posted on this page with a revised effective date. Continued
              use of the platform after changes are published constitutes your acceptance of the
              revised Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              15. Contact Information
            </h2>
            <p className="leading-relaxed">
              For any questions regarding these Terms and Conditions, please contact us at:
            </p>
            <div className="mt-4 space-y-2">
              <p>
                <span className="text-[#112318] font-medium">Email: </span>
                <a href="mailto:info@vinepass.co.za" className="text-[#1F3D2B] font-semibold hover:underline" data-testid="terms-contact-email">
                  info@vinepass.co.za
                </a>
              </p>
              <p>
                <span className="text-[#112318] font-medium">Website: </span>
                <a href="https://vinepass.co.za" className="text-[#1F3D2B] font-semibold hover:underline">
                  vinepass.co.za
                </a>
              </p>
            </div>
          </section>

          <section className="bg-[#F4F6F3] rounded-xl p-6 border border-[#E5E9E6]">
            <p className="text-sm text-[#4A5D50] leading-relaxed">
              By using VinePass, you acknowledge that you have read, understood, and agreed to these
              Terms and Conditions.
            </p>
          </section>

          <CompanyDetailsBlock />

        </div>

        <div className="mt-16 pt-8 border-t border-[#E5E9E6] flex items-center justify-between">
          <Link to="/" className="text-[#1F3D2B] font-semibold hover:underline text-sm" data-testid="terms-back-home">
            &larr; Back to Home
          </Link>
          <Link to="/privacy" className="text-[#1F3D2B] font-semibold hover:underline text-sm" data-testid="terms-to-privacy">
            Privacy Policy &rarr;
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
